import { type ReactNode, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Agent } from '../../agents/registry.ts';
import { useAuth } from '../../auth/useAuth.ts';
import type { AuthUser } from '../../auth/AuthContext.ts';
import type { ChatItem, ChatMsg, ChatStatus, ChatTool } from './useAgentChat.ts';
import { Link } from 'react-router-dom';
import { formatMessage } from './formatInline.ts';
import { redactEmailPreamble } from './emailMessage.ts';
import WorkflowRunProgressCard from './WorkflowRunProgress.tsx';
import type { WorkflowRunProgress } from './useWorkflowRunProgress.ts';
import { redactSmsPreamble } from './smsMessage.ts';
import { useSpeechInput } from './useSpeechInput.ts';
import DocumentCard from './DocumentCard.tsx';
import ImageJobCard from './ImageJobCard.tsx';
import ConfirmCard from './ConfirmCard.tsx';
import PreparingNotice from './PreparingNotice.tsx';
import DailyLimitNotice from './DailyLimitNotice.tsx';
import UploadCard from './UploadCard.tsx';
import './ChatPanel.css';

function argText(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

/** Thumbs up/down feedback under a settled agent message. Greyed and enabled
 *  until the user picks one; on click the choice fills in and BOTH buttons
 *  lock (disabled), so a rating is final and mutually exclusive. */
function FeedbackButtons({
  feedback,
  onRate,
}: {
  feedback: 'up' | 'down' | undefined;
  onRate: (rating: 'up' | 'down') => void;
}) {
  const rated = feedback !== undefined;

  return (
    <div className="om-msg__feedback" role="group" aria-label="Was this response helpful?">
      <button
        type="button"
        className="om-feedback om-feedback--up"
        aria-label="Good response"
        aria-pressed={feedback === 'up'}
        disabled={rated}
        onClick={() => onRate('up')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M7 10v10H4V10h3Zm3 0 4-7a2 2 0 0 1 3 1.7V9h3.2a2 2 0 0 1 2 2.4l-1.4 7A2 2 0 0 1 20.8 20H10V10Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        type="button"
        className="om-feedback om-feedback--down"
        aria-label="Bad response"
        aria-pressed={feedback === 'down'}
        disabled={rated}
        onClick={() => onRate('down')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M17 14V4h3v10h-3Zm-3 0-4 7a2 2 0 0 1-3-1.7V15H3.8a2 2 0 0 1-2-2.4l1.4-7A2 2 0 0 1 5.2 4H14v10Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}

/** First + last initials from the signed-in user's name, falling back to the
 *  email's leading character, then a generic "YOU" when no name is available. */
function userInitials(user: AuthUser | null): string {
  const given = user?.givenName?.trim();
  const family = user?.familyName?.trim();
  const initials = `${given?.[0] ?? ''}${family?.[0] ?? ''}`.toUpperCase();
  if (initials) {
    return initials;
  }
  const email = user?.email.trim();
  return email ? email[0].toUpperCase() : 'YOU';
}

interface ChatPanelProps {
  agent: Agent;
  items: ChatItem[];
  status: ChatStatus;
  /** Live "what the agent is doing now" label (Thinking… / Searching the web… /
   *  Verifying facts…), shown in the typing bubble so a long turn never looks
   *  stuck. Null hides the label (the animated dots remain). */
  activity?: string | null;
  onSend: (text: string) => void;
  /** Re-send a turn that failed (network drop), clearing its error notice. */
  onRetry: (text: string, errorId: number) => void;
  /** Record a thumbs up/down on an agent message (id + rating). */
  onFeedback: (messageId: number, rating: 'up' | 'down') => void;
  /** Start a fresh session (the "New" quickstart). */
  onNewSession: () => void;
  /** Optional strip between the transcript and the composer (e.g. the live
   *  workflow-run progress card). */
  /** Live workflow-run progress, rendered INLINE in the transcript at the
   *  point the run started — completion messages append naturally below it
   *  (user feedback: the old above-composer banner read as an overlay that
   *  hid results until dismissed). */
  runProgress?: WorkflowRunProgress | null;
  onRunProgressDismiss?: () => void;
  /** Guest console (tiers T8): hides quickstarts that make no sense without
   *  saved sessions (Recap). */
  guest?: boolean;
  /** Clicking the agent banner returns to the agent chooser (/app). */
  onChooseAgent?: () => void;
  /** Daily-usage snapshot for the allowance chip (signed-in console). */
  usage?: UsageChipData | null;
  /** Optional strip at the very top of the stream (the guest/free ad unit).
   *  The parent decides eligibility; the panel just renders it. */
  topSlot?: ReactNode;
  /** Upload a file the user picked in the composer. Omitted (or guest) hides
   *  the attach button entirely — guests have no document store. */
  onPickFile?: (file: File) => void;
  /** Abort the upload currently in flight (the card's Cancel button). */
  onCancelUpload?: () => void;
}

/** Display text for a user turn: inbound email/SMS preambles redact to the
 *  body (display-only); agent turns pass through. Returns the channel too so
 *  the bubble can render its chip. Module-level: keeps ChatPanel's render
 *  under the complexity pin. */
/** Chat items whose cards need no panel-level wiring (no callbacks into the
 *  panel's send/confirm machinery) — rendered via one branch in the items map
 *  to keep it inside the complexity budget. The type predicate keeps the
 *  map's fallthrough (ToolCard) narrowing intact. */
type SelfContainedItem = Extract<ChatItem, { kind: 'preparing' | 'imageJob' }>;

function isSelfContained(item: ChatItem): item is SelfContainedItem {
  return item.kind === 'preparing' || item.kind === 'imageJob';
}

function renderSelfContainedItem(item: SelfContainedItem): ReactNode {
  if (item.kind === 'preparing') {
    return <PreparingNotice key={item.id} mode={item.mode} />;
  }
  return <ImageJobCard key={item.id} jobId={item.jobId} prompt={item.prompt} turnTs={item.turnTs} />;
}

function displayTextOf(item: Extract<ChatItem, { kind: 'msg' }>): {
  text: string;
  channel: 'email' | 'sms' | null;
} {
  if (item.role !== 'user') {
    return { text: item.text, channel: null };
  }
  const email = redactEmailPreamble(item.text);
  if (email.isEmail) {
    return { text: email.body, channel: 'email' };
  }
  const sms = redactSmsPreamble(item.text);
  if (sms.isSms) {
    return { text: sms.body, channel: 'sms' };
  }
  return { text: item.text, channel: null };
}

/** Compact token count for the usage chip: 12400 → "12.4k". */
function fmtTokens(n: number): string {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  }
  return String(n);
}

interface UsageChipData {
  used: number;
  limit: number;
  remaining: number;
  omUsedToday?: number;
  omBudget?: number;
}

/** Daily usage above the composer: OM tokens (with upgrade nudge) when the
 *  runtime reports them, else the enforced prompt allowance. */
function UsageChip({ usage }: { usage?: UsageChipData | null }) {
  if (usage == null) {
    return null;
  }
  if (usage.omBudget !== undefined && usage.omUsedToday !== undefined) {
    const pct = Math.min(100, (usage.omUsedToday / Math.max(1, usage.omBudget)) * 100);
    return (
      <div className="om-usage-chip" title="Your daily token usage">
        <span className="om-usage-chip__bar">
          <i style={{ width: `${String(pct)}%` }} />
        </span>
        {fmtTokens(usage.omUsedToday)} of {fmtTokens(usage.omBudget)} tokens today
        <Link className="om-usage-chip__upgrade" to="/pricing">
          Upgrade for more
        </Link>
      </div>
    );
  }
  // Admin/VIP: usage reported without a budget (unlimited) — show the count,
  // no cap, no upgrade nudge.
  if (usage.omUsedToday !== undefined && usage.omBudget === undefined) {
    return (
      <div className="om-usage-chip" title="Your token usage today (unlimited)">
        {fmtTokens(usage.omUsedToday)} tokens today
      </div>
    );
  }
  if (usage.limit > 0) {
    const pct = Math.min(100, (usage.used / usage.limit) * 100);
    return (
      <div className="om-usage-chip" title="Your daily usage allowance">
        <span className="om-usage-chip__bar">
          <i style={{ width: `${String(pct)}%` }} />
        </span>
        {usage.remaining} of {usage.limit} left today
      </div>
    );
  }
  return null;
}

export default function ChatPanel({
  agent,
  items,
  status,
  activity = null,
  onSend,
  onRetry,
  onFeedback,
  onNewSession,
  runProgress,
  onRunProgressDismiss,
  guest = false,
  onPickFile,
  onCancelUpload,
  onChooseAgent,
  usage,
  topSlot,
}: ChatPanelProps) {
  const { user } = useAuth();
  const initials = userInitials(user);
  const [value, setValue] = useState('');
  // Outcome of each in-chat delete confirmation (by item id), so a resolved
  // card shows its result instead of live Yes/No buttons.
  const [resolvedConfirms, setResolvedConfirms] = useState<
    Map<number, 'confirmed' | 'dismissed'>
  >(() => new Map());
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // The text that was in the composer when the current voice session began, so
  // each interim transcript is spliced onto it rather than stacking up.
  const speechBaseRef = useRef('');
  // Whether the transcript is currently pinned to the bottom. We only auto-scroll
  // on new content while pinned, so a user who scrolls up to read earlier
  // messages isn't yanked back down on every streamed token. (The brand-banner
  // reveal-on-scroll is owned by AgentConsole, which watches every panel's
  // scroll, not just this one.)
  const stickToBottom = useRef(true);
  const busy = status === 'streaming';

  // Splice a session transcript onto whatever the user had already typed when
  // the voice session began (shared by the live and the auto-submit paths).
  function composeWithSpeech(transcript: string): string {
    const base = speechBaseRef.current;
    const sep = base && !/\s$/.test(base) ? ' ' : '';
    return base + sep + transcript;
  }

  const speech = useSpeechInput((transcript) => setValue(composeWithSpeech(transcript)), {
    // When the speaker trails off, send what they said — no extra tap needed.
    onAutoSubmit: (transcript) => submitText(composeWithSpeech(transcript)),
  });

  function toggleTalk() {
    if (speech.listening) {
      speech.stop();
      return;
    }
    speechBaseRef.current = value;
    speech.start();
  }

  // One-tap recap: send a canned prompt as if the user typed it, so the agent
  // calls its `recall_previous_conversation` tool and summarizes the most
  // recent prior session. Mirrors submit()'s re-pin + stop-voice behavior.
  function recapPrevious() {
    if (busy) {
      return;
    }
    stickToBottom.current = true;
    speech.stop();
    onSend('Recap our previous conversation');
  }

  // One-tap file listing: ask the agent to enumerate the user's documents.
  // Mirrors recapPrevious()'s re-pin + stop-voice behavior.
  function listFiles() {
    if (busy) {
      return;
    }
    stickToBottom.current = true;
    speech.stop();
    onSend('List my documents');
  }

  // One-tap fresh start: drop the current conversation into history and open a
  // new session. Unlike Recap/Files it sends no message, so it stays available
  // mid-stream (switching sessions aborts the in-flight turn) — an escape hatch.
  function startNew() {
    speech.stop();
    onNewSession();
  }

  // Keep the transcript pinned to the latest message as it streams — but only
  // while the user is already at the bottom (see `stickToBottom`).
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [items]);

  // When the agent finishes responding (streaming → idle), return focus to the
  // composer so the user can keep typing without reaching for the mouse — desktop
  // only. On touch/mobile, auto-focusing pops the soft keyboard, which would cover
  // the just-arrived response and hurt readability. Gate on a precise pointer at
  // the desktop layout width (so a wide tablet in landscape is treated as mobile);
  // jsdom has no matchMedia, hence the guard.
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const justResponded = prevStatusRef.current === 'streaming' && status === 'idle';
    prevStatusRef.current = status;
    if (
      justResponded &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 961px) and (pointer: fine)').matches
    ) {
      textareaRef.current?.focus();
    }
  }, [status]);

  // Auto-grow the composer up to a cap.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) {
      return;
    }
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [value]);

  function submitText(raw: string) {
    const text = raw.trim();
    if (!text || busy) {
      return;
    }
    // Sending re-pins to the bottom so the user always sees their own message
    // and the reply that streams in, even if they'd scrolled up beforehand.
    stickToBottom.current = true;
    speech.stop();
    onSend(text);
    setValue('');
    speechBaseRef.current = '';
  }

  function submit() {
    submitText(value);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  }

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    // Track bottom-pinned state for the streaming auto-scroll: pinned while the
    // viewport bottom is within ~80px of the content bottom. Re-checked on every
    // scroll (including the programmatic pin, which keeps it true). The brand
    // banner's reveal-on-scroll is handled centrally in AgentConsole.
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  // Inline run-card anchoring: remember the last item id at the moment a run
  // appears; the card renders right after that item, so results that stream
  // in during/after the run land BELOW it in normal transcript order. A
  // fresh runId re-anchors; a vanished anchor item falls back to the top.
  const runAnchorRef = useRef<{ runId: string; afterId: number | null } | null>(null);
  if (runProgress != null && runAnchorRef.current?.runId !== runProgress.record.runId) {
    runAnchorRef.current = {
      runId: runProgress.record.runId,
      afterId: items.length > 0 ? items[items.length - 1].id : null,
    };
  }
  if (runProgress == null) {
    runAnchorRef.current = null;
  }
  // Keyed because it's spliced into the transcript's flatMap array below —
  // React warns on a keyless child there. Keying on the runId (not the frame)
  // keeps the card mounted as progress frames arrive, so its expanded excerpt
  // survives an update, while a new run gets a fresh card.
  const runCard =
    runProgress != null && onRunProgressDismiss !== undefined ? (
      <WorkflowRunProgressCard
        key={`wfrun-${runProgress.record.runId}`}
        progress={runProgress}
        onDismiss={onRunProgressDismiss}
      />
    ) : null;
  const runAnchorIdx =
    runCard !== null && runAnchorRef.current?.afterId != null
      ? items.findIndex((i) => i.id === runAnchorRef.current?.afterId)
      : -1;

  return (
    <main className="om-panel om-panel--chat">
      <button
        type="button"
        className="om-chat__head"
        onClick={() => onChooseAgent?.()}
        title="Switch agent"
        aria-label="Switch agent — back to the agent chooser"
      >
        <div className="om-agent-portrait">
          <img src={agent.image} alt={agent.name} />
        </div>
        <div className="om-chat__id">
          <h2>
            {agent.name}
            {/* The chat console is only reachable for an available agent, i.e.
                its live console is up — so the header reads Online. The public
                home rail / info page keep the registry's TRAINING status. */}
            <span className="om-chip om-chip--online">
              <span className="om-dot om-dot--ok" />
              Online
            </span>
          </h2>
          <div className="om-chat__id-role">
            {agent.role} · <span className="om-chat__id-num">{agent.agentNumber}</span>
          </div>
        </div>
        <span className="om-chat__switch" aria-hidden="true">
          Switch ⇄
        </span>
      </button>

      {topSlot}

      <div className="om-chat__scroll" ref={scrollRef} onScroll={handleScroll}>
        {runCard !== null && runAnchorIdx === -1 ? runCard : null}
        {items.flatMap((item, index) => {
          const after = runCard !== null && index === runAnchorIdx ? [runCard] : [];
          const renderOne = (node: ReactNode): ReactNode[] => [node, ...after];
          if (item.kind === 'msg') {
            // A share reply whose only content was the link + boilerplate
            // cleans to nothing (the DocumentCard carries it) — skip the empty
            // bubble, and the slot with it.
            return isEmptyBubble(item)
              ? null
              : renderOne(
                  <TranscriptMessage
                    key={item.id}
                    item={item}
                    agent={agent}
                    initials={initials}
                    rateable={isRateable(items, index, item)}
                    onFeedback={onFeedback}
                  />,
                );
          }
          if (item.kind === 'error') {
            return renderOne(
              <div key={item.id} className="om-msg__error" role="alert">
                <span className="om-dot om-dot--err" />
                <span className="om-msg__error-text">{item.message}</span>
                {item.retryText !== undefined ? (
                  <button
                    type="button"
                    className="om-msg__retry"
                    disabled={busy}
                    onClick={() => {
                      // Re-guard inside the callback: the JSX guard's
                      // narrowing doesn't reach this closure.
                      if (item.retryText !== undefined) {
                        onRetry(item.retryText, item.id);
                      }
                    }}
                  >
                    Retry
                  </button>
                ) : null}
              </div>
            );
          }
          if (item.kind === 'limit') {
            return renderOne(
              <DailyLimitNotice key={item.id} limit={item.limit} resetAt={item.resetAt} guest={guest} />
            );
          }
          if (item.kind === 'doc') {
            return renderOne(
              <DocumentCard
                key={item.id}
                name={item.name}
                url={item.url}
                expiresAt={item.expiresAt}
                onRequestNew={() =>
                  onSend(
                    `The download link for ${item.name} expired — please generate a new ` +
                      `share link for ${item.path || item.name}.`,
                  )
                }
              />
            );
          }
          if (isSelfContained(item)) {
            return renderOne(renderSelfContainedItem(item));
          }
          if (item.kind === 'confirm') {
            const outcome = resolvedConfirms.get(item.id);
            return renderOne(
              <ConfirmCard
                key={item.id}
                prompt={item.prompt}
                resolved={outcome}
                onConfirm={() => {
                  setResolvedConfirms((prev) => new Map(prev).set(item.id, 'confirmed'));
                  stickToBottom.current = true;
                  // Phrase as a confirmation of the PENDING delete, not a fresh
                  // "delete X" command — otherwise the model re-runs the gate
                  // (another confirmation_required) instead of confirming. The
                  // "without asking again" mirrors the proven cloud-e2e phrasing.
                  onSend('Yes, I confirm — delete it now without asking again.');
                }}
                onDismiss={() =>
                  setResolvedConfirms((prev) => new Map(prev).set(item.id, 'dismissed'))
                }
              />
            );
          }
          if (item.kind === 'upload') {
            return renderOne(
              <UploadCard
                key={item.id}
                item={item}
                agentName={agent.name}
                onCancel={onCancelUpload}
              />,
            );
          }
          return renderOne(<ToolCard key={item.id} tool={item} />);
        })}
        {/* Typing indicator: while a turn is in flight but no agent bubble is
            building yet (the model is thinking — can be minutes on a big
            context), show an animated "agent is typing" bubble so the wait
            never looks like a hang. It disappears the instant the first token
            streams in (which creates the real bubble) or the turn settles. */}
        {status === 'streaming' &&
        !items.some((it) => it.kind === 'msg' && it.role === 'agent' && it.streaming) ? (
          <div className="om-msg om-msg--agent om-msg--typing" aria-live="polite">
            <div className="om-msg__avatar om-msg__avatar--agent">
              <img src={agent.image} alt={agent.name} />
            </div>
            <div className="om-msg__body">
              <div className="om-typing__row">
                {activity !== null ? (
                  // A live activity label pulses on its own — don't ALSO run the
                  // three-dot animation (two animations at once reads as busy).
                  <span className="om-typing__activity">{activity}</span>
                ) : (
                  <div className="om-typing" aria-label={`${agent.name} is typing`}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>


      <UsageChip usage={usage} />
      <div className="om-composer">
        {/* Quick actions above the text field: a one-tap "Recap" that asks the
            agent to summarize the previous conversation, plus Files and New.
            Voice input lives inside the composer box (a mic on the left,
            mirroring the send button) — see the .om-composer__box below. */}
        <ComposerQuickActions
          guest={guest}
          busy={busy}
          onRecap={recapPrevious}
          onFiles={listFiles}
          onNew={startNew}
        />
        <div className="om-composer__box">
          {/* Voice input: an in-field mic on the LEFT, mirroring the send button
              on the right. Shown only where the Web Speech API works (Chromium +
              Safari) and until mic permission is denied for the session. */}
          {speech.supported && !speech.denied ? (
            <button
              type="button"
              className={`om-mic${speech.listening ? ' om-mic--live' : ''}`}
              title={speech.listening ? 'Stop voice input' : 'Speak your message'}
              aria-label={speech.listening ? 'Stop voice input' : 'Speak your message'}
              aria-pressed={speech.listening}
              disabled={busy}
              onClick={toggleTalk}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 11a7 7 0 0 0 14 0M12 18v3"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
          {/* File upload: sits immediately right of the mic, and becomes the
              only in-field control where the Web Speech API is unavailable.
              Deliberately NOT disabled while the agent is responding — the
              upload does not need the agent; only its announcement queues. */}
          {onPickFile && !guest ? <AttachButton onPick={onPickFile} /> : null}
          <textarea
            ref={textareaRef}
            rows={1}
            value={value}
            disabled={busy}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              busy ? `${agent.name} is responding…` : `Type your question for ${agent.name}…`
            }
          />
          <button type="button" className="om-send" title="Send" disabled={busy} onClick={submit}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M4 12L20 4L13 20L11 13L4 12Z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        {/* Guest agreement consent rides the composer (no checkbox — sending
            is the acceptance, so the notice must stay adjacent to send). */}
        {guest ? (
          <p className="om-composer__consent">
            By messaging, you agree to our <Link to="/legal/terms">Terms</Link>,{' '}
            <Link to="/legal/privacy">Privacy Policy</Link>, and{' '}
            <Link to="/legal/ai-consent">AI Notice</Link> — ads keep guest access free.
          </p>
        ) : null}
      </div>
    </main>
  );
}

/** Inline tool-call card. Collapsed by default — the header (name + status) is
 *  always shown, and the args/result body expands only when the user clicks.
 *  All values come from the agent stream, so they are rendered as escaped text
 *  (React) rather than raw HTML. */
function ToolCard({ tool }: { tool: ChatTool }) {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(tool.args);
  const hasBody = entries.length > 0 || Boolean(tool.summary);
  return (
    <div className={`om-toolcall${open ? ' om-toolcall--open' : ''}`}>
      <button
        type="button"
        className="om-toolcall__head"
        aria-expanded={open}
        disabled={!hasBody}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ic">⚙</span>
        tool · <span className="om-toolcall__name">{tool.name}</span>
        <span className="om-toolcall__state">
          {tool.returned ? (
            <>
              <span className={tool.ok ? 'om-dot om-dot--ok' : 'om-dot om-dot--err'} />
              <span className={tool.ok ? 'ok' : 'err'}>{tool.ok ? 'returned' : 'error'}</span>
            </>
          ) : (
            <>
              <span className="om-dot om-dot--run" />
              invoking
            </>
          )}
        </span>
        {hasBody ? (
          <span className="om-toolcall__chevron" aria-hidden="true">
            {open ? '▾' : '▸'}
          </span>
        ) : null}
      </button>
      {open && hasBody ? (
        <div className="om-toolcall__body">
          {entries.map(([key, val]) => (
            <div key={key}>
              <span className="k">{key}</span>: <span className="v">{argText(val)}</span>
            </div>
          ))}
          {tool.summary ? (
            <div>
              → <span className={tool.ok === false ? 'err' : 'ok'}>{tool.summary}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The composer's file-picker: a paperclip that opens a hidden `<input>`.
 *
 * Its own component so the composer's JSX stays flat — and so the input's
 * hiding, the same-file-twice reset, and the button live in one place.
 */
function AttachButton({ onPick }: { onPick: (file: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        // No `om-visually-hidden` utility exists in this repo, and one input
        // does not justify inventing a global class. `display:none` is avoided
        // because some browsers skip click() on such an input.
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked !== undefined) {
            onPick(picked);
          }
          // Reset so picking the same file twice fires onChange twice.
          event.target.value = '';
        }}
      />
      <button
        type="button"
        className="om-composer__iconbtn"
        title="Attach a file"
        aria-label="Attach a file"
        onClick={() => inputRef.current?.click()}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M21 11.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.4 3.4 0 0 1 4.8 4.8l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </>
  );
}

/** True when a message renders to nothing after cleaning (a share reply whose
 *  only content was the presigned link, which the DocumentCard carries). */
function isEmptyBubble(item: ChatMsg): boolean {
  const { text } = displayTextOf(item);
  return (
    !item.streaming &&
    formatMessage(text, { streaming: false }).trim() === '' &&
    !(item.citations && item.citations.length)
  );
}

/** Thumbs show only on settled agent answers — never the greeting (which has
 *  no preceding user turn) and never while streaming. */
function isRateable(items: readonly ChatItem[], index: number, item: ChatMsg): boolean {
  return (
    item.role === 'agent' &&
    !item.streaming &&
    items.slice(0, index).some((prior) => prior.kind === 'msg' && prior.role === 'user')
  );
}

/**
 * One message bubble in the transcript: avatar, channel chip, rendered
 * markdown, sources, and the thumbs control.
 *
 * Its own component because the transcript's item dispatch was at its
 * complexity ceiling — every branch of that chain competes for the same
 * budget, and this was by far the largest.
 */
function TranscriptMessage({
  item,
  agent,
  initials,
  rateable,
  onFeedback,
}: {
  item: ChatMsg;
  agent: Agent;
  initials: string;
  rateable: boolean;
  onFeedback: (messageId: number, rating: 'up' | 'down') => void;
}) {
  const { text: displayText, channel } = displayTextOf(item);
  const html = formatMessage(displayText, { streaming: item.streaming });
  return (
    <div className={`om-msg om-msg--${item.role}`}>
      {item.role === 'agent' ? (
        <div className="om-msg__avatar om-msg__avatar--agent">
          <img src={agent.image} alt={agent.name} />
        </div>
      ) : (
        <div className="om-msg__avatar om-msg__avatar--user">{initials}</div>
      )}
      <div className="om-msg__body">
        <ChannelChip channel={channel} />
        {/* Content is escaped in formatMessage before any markup; the
            streaming cursor is appended inside the rendered HTML. */}
        <div className="om-msg__md" dangerouslySetInnerHTML={{ __html: html }} />
        {item.citations && item.citations.length > 0 ? (
          <div className="om-msg__sources">
            <span className="om-msg__sources-label">Sources</span>
            {item.citations.map((c) => (
              <a
                key={`${c.toolUseId}:${c.url}`}
                className="om-source"
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                title={c.url}
              >
                {c.domain}
              </a>
            ))}
          </div>
        ) : null}
        {rateable ? (
          <FeedbackButtons
            feedback={item.feedback}
            onRate={(rating) => onFeedback(item.id, rating)}
          />
        ) : null}
      </div>
    </div>
  );
}

/** "via email" / "via SMS" marker on an inbound message, or nothing. */
function ChannelChip({ channel }: { channel: 'email' | 'sms' | null }) {
  if (channel === 'email') {
    return (
      <span className="om-msg__email-chip" title="Received by email">
        <span aria-hidden="true">✉</span> via email
      </span>
    );
  }
  if (channel === 'sms') {
    return (
      <span className="om-msg__email-chip" title="Received by text">
        <span aria-hidden="true">💬</span> via SMS
      </span>
    );
  }
  return null;
}

/**
 * The quick-action row above the composer: Recap, Files, New.
 *
 * Its own component so the panel's body stays under its complexity ceiling —
 * these three buttons are self-contained and share nothing with the composer
 * below them but their handlers.
 */
function ComposerQuickActions({
  guest,
  busy,
  onRecap,
  onFiles,
  onNew,
}: {
  guest: boolean;
  busy: boolean;
  onRecap: () => void;
  onFiles: () => void;
  onNew: () => void;
}) {
  return (
    <div className="om-composer__voice">
          {guest ? null : (
          <button
            type="button"
            className="om-recap"
            title="Recap our previous conversation"
            disabled={busy}
            onClick={onRecap}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 12a7 7 0 1 0 2.1-5M5 4v4h4"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Recap
          </button>
          )}
          <button
            type="button"
            className="om-recap"
            title="List my documents"
            disabled={busy}
            onClick={onFiles}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M13 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9l-6-6Z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
              <path
                d="M13 3v6h6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>
            Files
          </button>
          <button
            type="button"
            className="om-recap"
            title="Start a new session"
            onClick={onNew}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            New
          </button>
        </div>
  );
}
